/**
 * `string` and `text` widgets. Each keystroke applies live (the binding
 * coalesces them into one undo step); blur or Enter ends the edit. Placeholder
 * copy is marked so it renders as secondary text (rule 9). A mixed value is an
 * empty field with a dash placeholder, so typing starts fresh.
 */
import type { KeyboardEvent } from "react";
import { isPlaceholder } from "../format/placeholder";
import { FieldError, fieldAria } from "./fieldAria";
import { isMixed, MIXED_LABEL } from "./mixed";
import type { WidgetProps } from "./types";

export function StringWidget({ id, label, prop, value, onChange, onCommit, error }: WidgetProps) {
  const text = typeof value === "string" ? value : "";
  return (
    <span className="kn-field">
      <input
        id={id}
        type="text"
        className="kn-input"
        aria-label={label}
        value={text}
        {...mixedPlaceholder(value)}
        disabled={prop.readonly === true}
        data-placeholder={isPlaceholder(text)}
        {...fieldAria(id, error)}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onBlur={onCommit}
        onKeyDown={(event) => {
          commitOnEnter(event, onCommit);
        }}
      />
      <FieldError id={id} error={error} />
    </span>
  );
}

export function TextWidget({ id, label, prop, value, onChange, onCommit, error }: WidgetProps) {
  const text = typeof value === "string" ? value : "";
  return (
    <span className="kn-field">
      <textarea
        id={id}
        className="kn-input kn-input--multiline"
        aria-label={label}
        value={text}
        rows={Math.min(6, Math.max(2, Math.ceil(text.length / 34)))}
        {...mixedPlaceholder(value)}
        disabled={prop.readonly === true}
        data-placeholder={isPlaceholder(text)}
        {...fieldAria(id, error)}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onBlur={onCommit}
      />
      <FieldError id={id} error={error} />
    </span>
  );
}

/** The dash placeholder of a mixed text or number field (its value is empty). */
export function mixedPlaceholder(value: unknown) {
  return isMixed(value) ? { placeholder: MIXED_LABEL } : {};
}

/** Enter ends the edit in single-line fields (Unreal's details panel behavior). */
export function commitOnEnter(event: KeyboardEvent, onCommit: () => void): void {
  if (event.key === "Enter") onCommit();
}
